import React, { useEffect, useState } from 'react';
import Link from 'components/link';
import Button, { PRIMARY_THEME } from 'components/control/button';
import { buildClassName } from 'utils/build-class-name';
import styles from './style.module.scss';

export type FloatingActionButtonProps = {
  /** The label text for the button */
  label: string;
  /** The destination URL for the button */
  destination: string;
  /** Optional click handler for analytics tracking */
  onClick?: () => void;
  /** Optional aria-label for accessibility (defaults to label) */
  ariaLabel?: string;
  /** Optional className for custom styling */
  className?: string;
  /** Whether to open link in new tab (default: true) */
  openInNewTab?: boolean;
  /** Delay before showing the button in milliseconds (default: 300) */
  entranceDelay?: number;
  /** Whether to show button only after user scrolls (default: false) */
  showAfterScroll?: boolean;
};

/**
 * Floating Action Button component
 * 
 * A prominent, persistent button that remains visible throughout the page scroll.
 * Positioned at the bottom right on desktop and bottom center on mobile.
 * 
 * Features:
 * - Fixed positioning with smooth entrance animation
 * - Responsive sizing (larger on desktop, compact on mobile)
 * - Accessible (keyboard navigation, screen reader support)
 * - Click tracking support for analytics
 * - Customizable appearance and behavior
 * 
 * @example
 * ```tsx
 * <FloatingActionButton
 *   label="Join Now"
 *   destination="https://example.com"
 *   onClick={() => trackEvent('fab_click')}
 * />
 * ```
 */
export default function FloatingActionButton({
  label,
  destination,
  onClick,
  ariaLabel,
  className,
  openInNewTab = true,
  entranceDelay = 300,
  showAfterScroll = false,
}: FloatingActionButtonProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);

  // Track scroll events if showAfterScroll is enabled
  useEffect(() => {
    if (!showAfterScroll) return;

    const handleScroll = () => {
      if (window.scrollY > 0) {
        setHasScrolled(true);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [showAfterScroll]);

  // Trigger entrance animation after component mounts (and scroll condition is met)
  useEffect(() => {
    if (showAfterScroll && !hasScrolled) return;

    const timer = setTimeout(() => {
      setIsVisible(true);
    }, entranceDelay);

    return () => clearTimeout(timer);
  }, [entranceDelay, showAfterScroll, hasScrolled]);

  const linkProps = openInNewTab
    ? { target: '_blank', rel: 'noopener noreferrer' }
    : {};

  return (
    <div 
      className={buildClassName(
        styles.component,
        isVisible && styles.visible,
        className
      )}
      data-testid="floating-action-button"
    >
      <Link 
        href={destination}
        {...linkProps}
        onClick={onClick}
        aria-label={ariaLabel || label}
      >
        <Button 
          label={label}
          theme={PRIMARY_THEME}
          className={styles.button}
        />
      </Link>
    </div>
  );
}

